import { el, mount } from './dom';
import { has, t } from '@core/i18n';
import { getGame } from '@core/game-registry';
import { TurnManager } from '@core/turn-manager';
import { loadSession, saveSession, recordStat, deleteSession } from '@core/session';
import { navigate } from './router';
import { assetUrl } from './assets';
import type { GameView } from '@core/types';
import { copy } from '@games/arcade/catalog';
import { cardinal, normalizeCountryInput } from '@games/globe/logic';

export async function renderPlay(gameId: string, sessionId: string): Promise<HTMLElement> {
  const game = getGame(gameId);
  if (!game) {
    return el('div', { class: 'p-6' }, [`Unknown game: ${gameId}`]);
  }

  const session = await loadSession(gameId, sessionId);
  if (!session) {
    queueMicrotask(() => navigate({ name: 'lobby' }));
    return el('div', { class: 'p-6' }, ['Session not found']);
  }

  // Load data before restoring the playable view, so failures reach the router.
  await game.loadAssets();
  let disposed = false;
  let submitting = false;
  const tm = TurnManager.restore(game, session);

  const root = el('div', { class: 'min-h-dvh', dataset: { theme: game!.id } });

  let handoffPending = tm.config.mode !== 'single';
  let lastFeedback = '';
  let lastError = '';

  function gameText(suffix: string, fallbackKey: string, vars: Record<string, string | number> = {}): string {
    const gameKey = `${game!.id}.${suffix}`;
    return t(has(gameKey) ? gameKey : fallbackKey, vars);
  }

  const boardContainer = el('div',
    game.id === 'globe'
      ? { class: 'card globe-board w-full max-w-xs sm:max-w-sm mx-auto aspect-square relative overflow-hidden p-0' }
      : { class: 'w-full' },
  );
  let view: GameView | null = null;

  function rebuildBoard(): void {
    if (disposed) return;
    if (view) view.destroy();
    view = game!.renderBoard(boardContainer, tm.snapshot().state, {
      onGuess: (g) => void submit(typeof g === 'string' ? g : JSON.stringify(g)),
      onRequestHint: game!.revealHint
        ? () => {
            if (handoffPending || submitting || tm.isGameOver()) return;
            tm.patchState((s) => game!.revealHint!(s));
            void saveSession(tm.serialize());
            if (view?.update) view.update(tm.snapshot().state);
            else rebuildBoard();
            render();
          }
        : undefined,
    });
  }

  async function submit(raw: string): Promise<void> {
    if (handoffPending || submitting || disposed || tm.isGameOver()) return;
    submitting = true;
    try { await submitGuess(raw); }
    catch (error) { lastError = copy('Non riesco a salvare la partita. Riprova.', 'Unable to save the game. Please try again.'); console.error(error); if (!disposed) render(); }
    finally { submitting = false; }
  }

  async function submitGuess(raw: string): Promise<void> {
    const r = tm.submit(raw);
    if (!r.ok) {
      lastError =
        r.reason === 'duplicate'
          ? game!.id === 'globe' ? t('globe.duplicate_country') : t('animals.duplicate_guess')
          : game!.id === 'globe' ? copy('Paese non riconosciuto o nome ambiguo: scegli un suggerimento.', 'Country not recognized or ambiguous: choose a suggestion.') : t('animals.unknown_guess');
      // In caso di duplicato il turno NON avanza: stesso giocatore riprova.
      render();
      return;
    }
    lastError = '';
    lastFeedback = r.result.feedback ?? '';
    await saveSession(tm.serialize());
    if (disposed) return;
    if (r.won) {
      const snap = tm.snapshot();
      const winner = snap.history[snap.history.length - 1];
      if (winner) {
        await recordStat({
          gameType: game!.id,
          entityId: winner.byEntity.id,
          attempts: snap.history.length,
          won: true,
        });
      }
      renderWin();
      return;
    }
    handoffPending = tm.config.mode !== 'single';
    if (view?.update) {
      view.update(tm.snapshot().state);
    } else {
      rebuildBoard();
    }
    render();
  }

  async function surrender(): Promise<void> {
    if (!confirm(gameText('surrender.confirm', 'common.surrender.confirm'))) return;
    tm.surrender();
    await saveSession(tm.serialize());
    if (view?.update) view.update(tm.snapshot().state);
    handoffPending = false;
    renderEnd();
  }

  async function newGame(): Promise<void> {
    await deleteSession(game!.id, sessionId);
    navigate({ name: 'setup', gameId: game!.id });
  }

  function renderHistory(): HTMLElement {
    const snap = tm.snapshot();
    // Ordina per distanza crescente (più vicino in cima); le guess senza km (es. vittoria) vanno in fondo.
    const items = snap.history.slice().sort((a, b) => {
      const ka = a.result.score ?? Infinity;
      const kb = b.result.score ?? Infinity;
      return ka - kb;
    });
    return el(
      'div',
      { class: 'card globe-history-card max-h-64 overflow-y-auto' },
      items.length === 0
        ? [el('p', { class: 'text-slate-400 text-sm' }, ['—'])]
        : items.map((h) => {
            const guess = h.guess as { name?: string; cca2?: string; flag?: string; iso3?: string };
            const guessName = guess.name ?? String(h.guess);
            const cca2 = guess.cca2?.toLowerCase() ?? '';
            const flagEmoji = guess.flag ?? '';
            const km = h.result.score;
            const color = (h.result.meta as { color?: string } | undefined)?.color ?? '#94a3b8';

            // <img> con bandiera SVG ufficiale; se fallisce (SVG mancante) mostra l'emoji.
            const flagEl = (() => {
              if (cca2) {
                const img = document.createElement('img');
                img.src = assetUrl(`flags/${cca2}.svg`);
                img.setAttribute('width', '20');
                img.setAttribute('height', '14');
                img.className = 'inline-block shrink-0 rounded-sm object-cover';
                img.alt = guessName;
                const fallback = el('span', { class: 'shrink-0 text-base leading-none hidden' }, [flagEmoji]);
                img.addEventListener('error', () => {
                  img.classList.add('hidden');
                  fallback.classList.remove('hidden');
                });
                const wrap = el('span', { class: 'shrink-0 flex items-center' });
                wrap.append(img, fallback);
                return wrap;
              }
              return flagEmoji ? el('span', { class: 'shrink-0 text-base leading-none' }, [flagEmoji]) : null;
            })();

            return el(
              'div',
              {
                class:
                  'flex items-center gap-2 py-1 border-b border-white/5 last:border-0 text-sm cursor-pointer hover:bg-white/5 rounded transition-colors',
                on: {
                  click: () => {
                    const iso3 = guess.iso3;
                    if (iso3) view?.focusCountry?.(iso3);
                  },
                },
              },
              [
                el('span', {
                  class: 'inline-block w-3 h-3 rounded-full shrink-0',
                  style: `background:${color}`,
                }),
                flagEl,
                el('span', { class: 'flex-1 truncate font-medium' }, [guessName]),
                km !== undefined
                  ? el('span', { class: 'text-xs tabular-nums shrink-0', style: `color:${color}` }, [
                      `${km.toLocaleString()} km`,
                    ])
                  : null,
                game!.id === 'globe' && !h.result.correct && typeof h.result.meta?.bearing === 'number'
                  ? el('span', { class: 'text-xs shrink-0', title: t('globe.bearing.' + cardinal(h.result.meta.bearing)) }, [
                      `${['↑','↗','→','↘','↓','↙','←','↖'][Math.round(h.result.meta.bearing / 45) % 8]} ${cardinal(h.result.meta.bearing)}`,
                    ]) : null,
              ],
            );
          }),
    );
  }

  function handoffOverlay(): HTMLElement {
    const snap = tm.snapshot();
    return el(
      'div',
      {
        class:
          'absolute inset-0 z-50 bg-bg/95 backdrop-blur flex flex-col items-center justify-center p-6 text-center gap-4',
      },
      [
        el('p', { class: 'text-slate-400 uppercase tracking-widest text-xs' }, [
          t('turn.handoff.subtitle'),
        ]),
        el('h2', { class: 'text-4xl sm:text-6xl font-bold' }, [
          t('turn.handoff.title', { name: snap.entityLabel }),
        ]),
        snap.entity.kind === 'team'
          ? el('p', { class: 'text-slate-300' }, [`(${snap.player.name})`])
          : null,
        el(
          'button',
          {
            class: 'btn-primary mt-4',
            on: {
              click: () => {
                handoffPending = false;
                render();
              },
            },
          },
          [t('turn.handoff.continue')],
        ),
      ],
    );
  }

  function render(): void {
    if (disposed) return;
    const keepInputFocus = document.activeElement instanceof HTMLInputElement && root.contains(document.activeElement);
    const snap = tm.snapshot();
    // Accessible suggestions support keyboard, touch and country/animal aliases.
    const acValues = game!.autocompleteValues?.() ?? [];
    const listId = `${game!.id}-suggestions`;
    const suggestions = el('div', { class: 'suggestion-list', id: listId });
    suggestions.setAttribute('role', 'listbox');
    let selected = -1, values: string[] = [];
    const inputAttrs: Record<string, string> = {
      class: 'input flex-1',
      placeholder: game!.id === 'globe' ? copy('Cerca un paese…', 'Search for a country…') : copy('Cerca un animale…', 'Search for an animal…'),
      autocomplete: 'off',
    };
    const input = el('input', {
      ...inputAttrs,
      on: {
        input: () => {
          const q = normalizeCountryInput(input.value); selected = -1;
          values = q ? game!.suggestGuesses?.(input.value) ?? acValues.filter((v) => normalizeCountryInput(v).includes(q)).slice(0, 8) : [];
          showSuggestions();
        },
        blur: () => { setTimeout(() => { suggestions.replaceChildren(); input.setAttribute('aria-expanded', 'false'); }, 150); },
        keydown: (ev) => {
          const e = ev as KeyboardEvent;
          if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && values.length) {
            e.preventDefault(); selected = (selected + (e.key === 'ArrowDown' ? 1 : -1) + values.length) % values.length; showSuggestions();
          } else if (e.key === 'Escape') { values = []; showSuggestions(); }
          else if (e.key === 'Enter') {
            e.preventDefault();
            const v = selected >= 0 ? values[selected] : input.value.trim();
            if (v) void submit(v);
            (ev.target as HTMLInputElement).value = '';
          }
        },
      },
    });
    input.setAttribute('aria-label', game!.id === 'globe' ? copy('Nome del paese', 'Country name') : copy('Nome dell’animale', 'Animal name'));
    input.setAttribute('role', 'combobox'); input.setAttribute('aria-autocomplete', 'list'); input.setAttribute('aria-controls', listId); input.setAttribute('aria-expanded', 'false');
    function showSuggestions(): void {
      suggestions.replaceChildren(...values.map((value, idx) => {
        const option = el('button', { class: `suggestion-item ${idx === selected ? 'selected' : ''}`, id: `${listId}-${idx}`, on: { mousedown: (e) => e.preventDefault(), click: () => { input.value = value; values = []; showSuggestions(); void submit(value); } } }, [value]);
        option.setAttribute('role', 'option'); option.setAttribute('aria-selected', String(idx === selected)); option.tabIndex = -1; return option;
      }));
      input.setAttribute('aria-expanded', String(values.length > 0));
      if (selected >= 0) input.setAttribute('aria-activedescendant', `${listId}-${selected}`); else input.removeAttribute('aria-activedescendant');
    }
    const inputWrap = el('div', { class: 'suggestion-wrap' }, [input, suggestions]);
    const hints = game!.getHints?.(snap.state, { turnNumber: snap.history.length }) ?? [];
    const nearest = snap.history.slice().sort((a, b) => (a.result.score ?? Infinity) - (b.result.score ?? Infinity))[0];
    const quizHelp = game!.id === 'globe' ? el('div', { class: 'grid gap-2' }, [
      el('div', { class: 'quiz-summary' }, [el('span', { class: 'heat-scale' }), el('span', {}, [copy('Chiaro = lontano · Rosso = vicino', 'Pale = far · Red = near')]), nearest ? el('strong', {}, [`${copy('Migliore', 'Best')}: ${(nearest.guess as { name: string }).name} · ${nearest.result.score?.toLocaleString()} km`]) : null]),
      el('div', { class: 'globe-help' }, [
        el('span', {}, [hints.length ? hints.map((h) => h.text).join(' · ') : copy('Gli indizi geografici si sbloccano dopo 3 tentativi.', 'Geographical hints unlock after 3 guesses.')]),
        el('button', { disabled: snap.history.length < 3 || hints.length >= 2, on: { click: () => { tm.patchState((s) => game!.revealHint!(s)); void saveSession(tm.serialize()); render(); } } }, [hints.length >= 2 ? copy('Indizi esauriti', 'All hints revealed') : copy('💡 Indizio', '💡 Hint')]),
      ]),
    ]) : null;

    const main = el('main', { class: 'mx-auto max-w-3xl p-4 sm:p-6 grid gap-4 relative' }, [
      el('header', { class: 'flex items-center justify-between gap-2' }, [
        el(
          'button',
          { class: 'btn-ghost', on: { click: () => navigate({ name: 'lobby' }) } },
          ['←'],
        ),
        el('div', { class: 'text-center min-w-0 flex-1' }, [
          el('div', { class: 'text-xs text-slate-400' }, [
            t('turn.attempt', { n: snap.history.length }),
          ]),
          el('div', { class: 'font-semibold truncate flex items-center justify-center gap-1' }, [
            game!.metadata.iconUrl
              ? (() => { const img = document.createElement('img'); img.src = assetUrl(game!.metadata.iconUrl); img.alt = ''; img.className = 'w-5 h-5 object-contain shrink-0'; return img; })()
              : document.createTextNode(game!.metadata.icon + ' '),
            document.createTextNode(game!.metadata.name),
          ]),
          el('div', { class: 'text-xs text-slate-400' }, [snap.entityLabel]),
        ]),
        el('div', { class: 'flex gap-1' }, [
          el(
            'button',
            {
              class: 'btn-ghost text-xs',
              title: gameText('surrender', 'common.surrender'),
              on: { click: () => void surrender() },
            },
            ['🏳️'],
          ),
          el(
            'button',
            {
              class: 'btn-ghost text-xs',
              title: gameText('new_game', 'common.new_game'),
              on: { click: () => void newGame() },
            },
            ['🔄'],
          ),
        ]),
      ]),
      snap.history.length === 0 && game!.id === 'globe' ? el('div', { class: 'quiz-intro' }, [el('strong', {}, [copy('Un pianeta. Un paese da scovare.', 'One planet. One country to find.')]), copy('Scrivi un paese: distanza e freccia ti guidano verso la soluzione. Rosso intenso significa vicino; 0 km può indicare un confinante. Trascina il globo e tocca un tentativo per rivederlo.', 'Type a country: distance and arrows guide you to the answer. Deep red means close; 0 km may mean a neighbour. Drag the globe and tap a guess to revisit it.')]) : null,
      boardContainer,
      quizHelp,
      el('div', { class: 'theme-divider' }),
      el('div', { class: 'flex gap-2' }, [
        inputWrap,
        el(
          'button',
          {
            class: 'btn-primary',
            on: {
              click: () => {
                const v = input.value.trim();
                if (v) void submit(v);
                input.value = '';
              },
            },
          },
          [t('turn.guess.submit')],
        ),
      ]),
      lastError
        ? el('div', { class: 'text-rose-300 text-sm' }, [lastError])
        : lastFeedback
          ? el('div', { class: 'theme-feedback text-sm' }, [lastFeedback])
          : null,
      game!.metadata.rendersOwnHistory ? null : renderHistory(),
      handoffPending ? handoffOverlay() : null,
    ]);

    mount(root, main);
    if (keepInputFocus && !handoffPending) input.focus({ preventScroll: true });
  }

  function renderWin(): void {
    if (disposed) return;
    view?.destroy(); view = null;
    const snap = tm.snapshot();
    const winner = snap.history[snap.history.length - 1];
    const winnerName = winner
      ? tm.config.players.find((p) => p.id === winner.byPlayerId)?.name ?? '?'
      : '?';
    mount(
      root,
      el('main', { class: 'mx-auto max-w-3xl p-4 sm:p-6' }, [
        el('div', { class: 'grid gap-4 text-center mt-12' }, [
          el('div', { class: 'text-6xl' }, ['🎉']),
          el('h1', { class: 'text-3xl font-bold theme-win-title' }, [t('win.title')]),
          el('p', { class: 'text-2xl font-bold' }, [tm.getTargetLabel() ?? '']),
          el('p', { class: 'text-slate-300' }, [
            `${winnerName} • ${t('win.summary', { n: snap.history.length })}`,
          ]),
          el('div', { class: 'flex gap-2 justify-center mt-4' }, [
            el(
              'button',
              {
                class: 'btn-ghost',
                on: {
                  click: async () => {
                    await deleteSession(game!.id, sessionId);
                    navigate({ name: 'lobby' });
                  },
                },
              },
              [t('win.back')],
            ),
            el(
              'button',
              {
                class: 'btn-primary',
                on: {
                  click: async () => {
                    await deleteSession(game!.id, sessionId);
                    navigate({ name: 'setup', gameId: game!.id });
                  },
                },
              },
              [t('win.replay')],
            ),
          ]),
        ]),
      ]),
    );
  }

  function renderEnd(): void {
    const snap = tm.snapshot();
    const targetLabel = tm.getTargetLabel();
    mount(
      root,
      el('main', { class: 'mx-auto max-w-3xl p-4 sm:p-6 grid gap-4' }, [
        el('header', { class: 'flex items-center justify-between gap-2' }, [
          el(
            'button',
            { class: 'btn-ghost', on: { click: () => navigate({ name: 'lobby' }) } },
            ['←'],
          ),
          el('div', { class: 'text-center font-semibold flex-1 flex items-center justify-center gap-1' }, [
            game!.metadata.iconUrl
              ? (() => { const img = document.createElement('img'); img.src = assetUrl(game!.metadata.iconUrl); img.alt = ''; img.className = 'w-5 h-5 object-contain shrink-0'; return img; })()
              : document.createTextNode(game!.metadata.icon + ' '),
            document.createTextNode(gameText('surrendered.title', 'common.surrendered.title')),
          ]),
          el('span', { class: 'w-10' }, ['']),
        ]),
        el('div', { class: 'theme-divider' }),
        boardContainer,
        targetLabel
          ? el('div', { class: 'card text-center' }, [
              el('p', { class: 'text-slate-400 text-xs uppercase tracking-widest' }, [
                gameText('surrendered.title', 'common.surrendered.title'),
              ]),
              el('p', { class: 'text-xl font-bold mt-2' }, [
                gameText('target_was', 'common.target_was', { name: targetLabel }),
              ]),
              el('p', { class: 'text-slate-400 text-sm mt-1' }, [
                t('win.summary', { n: snap.history.length }),
              ]),
            ])
          : null,
        el('div', { class: 'flex gap-2 justify-center' }, [
          el(
            'button',
            {
              class: 'btn-ghost',
              on: {
                click: async () => {
                  await deleteSession(game!.id, sessionId);
                  navigate({ name: 'lobby' });
                },
              },
            },
            [t('win.back')],
          ),
          el(
            'button',
            {
              class: 'btn-primary',
              on: { click: () => void newGame() },
            },
            [gameText('new_game', 'common.new_game')],
          ),
        ]),
        game!.metadata.rendersOwnHistory ? null : renderHistory(),
      ]),
    );
  }

  rebuildBoard();
  // Se la sessione è stata caricata già finita (vinta o resa), salta direttamente alla schermata finale.
  if (tm.isGameOver()) {
    if (tm.isSurrendered()) renderEnd();
    else renderWin();
  } else {
    render();
  }

  (root as HTMLElement & { dispose?: () => void }).dispose = () => { disposed = true; view?.destroy(); view = null; };

  return root;
}
